import React from 'react';
import cn from 'classnames';
import {createUseStyles} from 'react-jss';

export interface Option {
  id: string
  value: string
}

type Props = {
  options: Option[]
  selectedValue: string
  className?: string
  onChange?: (value: string) => void
}

export default function RadioGroup({options, selectedValue, className, onChange}: Props) {
  const [activeValue, setActiveValue] = React.useState(selectedValue);

  const handleClick = (value: string) => {
    if (onChange) {
      onChange(value);
    }
    setActiveValue(value);
  };

  return (
    <fieldset className={className}>
      <ul role="radiogroup" aria-labelledby="radiogroup-label" className="flex flex-row text-blue">
        {
          options.map((option, index) => {
            const mayBePosition = index === options.length - 1 ? 'right' : 'center';
            const position = index === 0 ? 'left' : mayBePosition;
            return (
              <RadioGroupButton position={position} index={index} value={option.id} handleClick={handleClick}
                activeValue={activeValue} key={option.id}>{option.value}</RadioGroupButton>
            );
          })
        }
      </ul>
    </fieldset>
  );
}

type RadioGroupProps = {
  value: string
  position: string
  index: number
  activeValue: string
  handleClick: (value: string) => void
}

function RadioGroupButton({
  value, position, index, activeValue, handleClick, children,
}: React.PropsWithChildren<RadioGroupProps>) {
  const active = value === activeValue;

  const styles = createUseStyles({
    radioButton: {
      minWidth: '80px',
    },
  })();

  return (
    <li id={`radiogroup-option-${index}`} tabIndex={index} role="radio" aria-checked="true"
      onClick={() => handleClick(value)}
      className={cn(
        'group relative cursor-pointer bg-white text-center',
        {'rounded-l-md': position === 'left'},
        {'rounded-r-md': position === 'right'},
        {'bg-blue text-white': active},
        styles.radioButton,
      )}>
      <div
        className={cn(
          'border border-blue px-6 py-2',
          {'rounded-l-md': position === 'left'},
          {'border-l-0': position !== 'left'},
          {'rounded-r-md': position === 'right'},
        )}>
        {children}
      </div>
    </li>
  );
}
